import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import GenericDropDown from '../../shared/common/GenericDropDown';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { textColor, THEME_BLACK } from '../../styles/basics/colors';
import { AnalyseOut, CompleteCollectionAnalyseOut } from '../../store/api';
import { LoanFormatIcon } from '../../shared/common/GildIcon';
import { useTranslation } from 'react-i18next';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../../shared/loanFormat/constants';

const StIcon = styled.i`
    font-size: 16px;
    line-height: 1 !important;
    margin-right: 5px;
`;

const StAnalyseButton = styled.a`
    display: inline-flex;
    align-items: center;
    margin: 0 5px 0 15px;

    ${textColor(THEME_BLACK)};

    :hover {
        ${textColor(THEME_BLACK)};
    }
`;

const StAnalyseLabel = styled.span`
    font-size: 14px;
    line-height: 1;
`;

const StGenericDropDown = styled(GenericDropDown)`
    display: flex;
`;

interface GenericDropDownProps<T> {
    onAnalyseClicked: () => void;
    showDropdown: boolean;
    title?: string;
    label?: string;
    className?: string;
    tableComponent: FC<{ analysis: T }>;
    analysis:
        | {
              id: string;
              analyses: {
                  EAUDIOBOOKS: T;
                  EBOOKS: T;
                  EPRESS: T;
              };
          }
        | undefined;
    isLoading: boolean;
}

function AnalyseDropDown<T extends AnalyseOut | CompleteCollectionAnalyseOut>({
    analysis,
    label,
    title = 'Analyse',
    tableComponent,
    className,
    onAnalyseClicked = () => {},
    showDropdown,
    isLoading = false
}: GenericDropDownProps<T>) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { t } = useTranslation();

    const TableComponent = tableComponent;

    function buildAnalysisHeader(
        analysis: AnalyseOut | CompleteCollectionAnalyseOut | undefined,
        loanformat: {
            requestKey?: string | null;
            key?: string;
            name: any;
            singularName?: string | null;
            icon: any;
            urlKey?: string;
        }
    ) {
        if (
            !analysis ||
            ((analysis as AnalyseOut).amount ?? (analysis as CompleteCollectionAnalyseOut).totalCopies ?? 0) <= 0
        ) {
            return null;
        }

        return (
            <tr>
                <th colSpan={5} style={{ fontSize: '14px' }}>
                    <div className="display-inline-flex">
                        <LoanFormatIcon icon={loanformat.icon} className="loanformat-icon-table" />
                        <span>
                            {t(`components.analyse.${loanformat.name}`, {
                                defaultValue: loanformat.name
                            })}
                        </span>
                    </div>
                </th>
            </tr>
        );
    }

    const onOpen = useCallback(() => {
        setIsDropdownOpen(true);
        onAnalyseClicked();
    }, [onAnalyseClicked]);

    const onClose = useCallback(() => {
        setIsDropdownOpen(false);
        onAnalyseClicked();
    }, [onAnalyseClicked]);

    const mainClasses = classNames(className, 'analyse', 'default-dd-nav');

    const analyseTable = (
        <div className="default-dd">
            <div className="loading-container" style={{ display: isLoading ? 'block' : 'none' }}>
                <LoadingSpinner width={22} /> {t('components.analyse.analysing')}
            </div>
            <div className="default-dd-content" style={{ display: isLoading ? 'none' : 'block' }}>
                <table className="tb-analyse">
                    <tbody>
                        {buildAnalysisHeader(analysis?.analyses.EBOOKS, LOANFORMAT_EBOOK_OBJECT)}
                        {analysis?.analyses.EBOOKS ? <TableComponent analysis={analysis?.analyses.EBOOKS} /> : null}
                        {buildAnalysisHeader(analysis?.analyses.EAUDIOBOOKS, LOANFORMAT_EAUDIOBOOK_OBJECT)}
                        {analysis?.analyses.EAUDIOBOOKS ? (
                            <TableComponent analysis={analysis?.analyses.EAUDIOBOOKS} />
                        ) : null}
                        {buildAnalysisHeader(analysis?.analyses.EPRESS, LOANFORMAT_EPRESS_OBJECT)}
                        {analysis?.analyses.EPRESS ? <TableComponent analysis={analysis?.analyses.EPRESS} /> : null}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <StGenericDropDown
            className={mainClasses}
            onOpenListener={onOpen}
            onCloseListener={onClose}
            showDropdown={showDropdown}
        >
            <StAnalyseButton data-toggle-dropdown title={title}>
                <StIcon className="material-icons">equalizer</StIcon>
                {label ? <StAnalyseLabel>{label}</StAnalyseLabel> : null}
                <StIcon className="material-icons">
                    {isDropdownOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </StIcon>
            </StAnalyseButton>
            {analyseTable}
        </StGenericDropDown>
    );
}

export default AnalyseDropDown;
